/**
 * Helper functions for sanitizing API responses
 */

/**
 * Sanitizes data to replace any NaN or Infinity values with 0
 * @param {any} obj - The object to sanitize
 * @returns {any} - The sanitized object
 */
export const sanitizeData = (obj) => {
  if (!obj) return obj;
  
  if (typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeData(item));
  }
  
  const result = {};
  for (const key in obj) {
    if (typeof obj[key] === 'number') {
      if (!isFinite(obj[key])) { // Check for NaN, Infinity, -Infinity
        result[key] = 0;
      } else {
        result[key] = obj[key];
      }
    } else if (typeof obj[key] === 'object') {
      result[key] = sanitizeData(obj[key]);
    } else {
      result[key] = obj[key];
    }
  }
  return result;
};

/**
 * Cleans a JSON string by replacing NaN and Infinity values with 0
 * @param {string} jsonStr - The JSON string to clean
 * @returns {string} - The cleaned JSON string
 */
export const cleanJsonString = (jsonStr) => {
  if (typeof jsonStr !== 'string') return jsonStr;
  
  return jsonStr
    .replace(/:\s*NaN\s*,/g, ': 0,')  // Replace NaN with 0
    .replace(/:\s*Infinity\s*,/g, ': 0,')  // Replace Infinity with 0
    .replace(/:\s*-Infinity\s*,/g, ': 0,')  // Replace -Infinity with 0
    .replace(/:\s*NaN\s*\}/g, ': 0}')  // NaN at the end of an object
    .replace(/:\s*Infinity\s*\}/g, ': 0}')  // Infinity at the end of an object
    .replace(/:\s*-Infinity\s*\}/g, ': 0}');  // -Infinity at the end of an object
};

/**
 * Safely parses a JSON string, handling errors and invalid values
 * @param {string} jsonStr - The JSON string to parse
 * @param {object} defaultValue - The default value to return if parsing fails
 * @returns {object} - The parsed object or default value
 */
export const safeJsonParse = (jsonStr, defaultValue = {}) => {
  if (typeof jsonStr !== 'string') return jsonStr;
  
  try {
    const cleanedStr = cleanJsonString(jsonStr);
    const parsed = JSON.parse(cleanedStr);
    return sanitizeData(parsed);
  } catch (error) {
    console.error('Failed to parse JSON string:', error);
    return defaultValue;
  }
};
